import { useState } from 'react';
import { Loader2, AlertTriangle, CheckCircle2, XCircle, Landmark } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const REQUISITION_STATUS_VARIANT = {
  linked: 'default',
  created: 'outline',
  expired: 'destructive',
  error: 'destructive',
};

export function GoCardlessStatusPanel() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const runCheck = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: d } = await diagnosticsAPI.gocardlessStatus();
      setData(d);
    } catch (err) {
      setError(getErrorMessage(err));
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Landmark className="h-5 w-5" />
          GoCardless Status
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Read-only per-connection status: requisition state, account and ingestion
          counts, current rate-limit remaining, and any account whose last sync errored.
        </p>

        <Button
          variant="outline"
          size="sm"
          onClick={runCheck}
          disabled={loading}
          data-testid="gc-status-run"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
          Check GoCardless Status
        </Button>

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-500">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </div>
        )}

        {data && (
          <div className="space-y-3" data-testid="gc-status-result">
            <p className="text-xs text-muted-foreground">{data.count} connection(s)</p>
            {(data.connections || []).map((conn) => (
              <div key={conn.connection_id} className="rounded border px-3 py-2 space-y-1.5" data-testid="gc-status-row">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{conn.institution_name || conn.connection_id}</span>
                  <Badge variant={REQUISITION_STATUS_VARIANT[conn.requisition_status] || 'outline'} className="text-[10px]">
                    {conn.requisition_status}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground">
                  {conn.account_count} account(s) · {conn.transactions_ingested_total} transaction(s) ingested
                  {' '}({conn.transactions_ingested_last_run} last run)
                </div>
                {conn.last_run && (
                  <div className="text-xs text-muted-foreground">
                    Last run: {conn.last_run.status}
                    {conn.last_run.finished_at && ` at ${new Date(conn.last_run.finished_at).toLocaleString()}`}
                  </div>
                )}
                {conn.rate_limit_remaining !== null && conn.rate_limit_remaining !== undefined && (
                  <div className="text-xs text-muted-foreground">
                    Rate limit remaining: <span className="font-mono">{conn.rate_limit_remaining}</span>
                    {conn.rate_limit_observed_at && ` (as of ${new Date(conn.rate_limit_observed_at).toLocaleString()})`}
                  </div>
                )}
                {conn.errored_accounts && conn.errored_accounts.length > 0 && (
                  <div className="space-y-1 pt-1">
                    {conn.errored_accounts.map((acc, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-xs text-red-600">
                        <XCircle className="h-3 w-3 shrink-0" />
                        <span className="font-mono">{acc.account_id}</span>
                        <span>{acc.status}{acc.endpoint ? ` (${acc.endpoint})` : ''}</span>
                      </div>
                    ))}
                  </div>
                )}
                {(!conn.errored_accounts || conn.errored_accounts.length === 0) && conn.last_run && (
                  <div className="flex items-center gap-1.5 text-xs text-green-600">
                    <CheckCircle2 className="h-3 w-3 shrink-0" />
                    No errored accounts in last run
                  </div>
                )}
              </div>
            ))}
            {(!data.connections || data.connections.length === 0) && (
              <p className="text-sm text-muted-foreground">No GoCardless connections yet.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
